import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, KeyRound, Mail } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAuth, isStaff } from "../../lib/auth";
import { useAntiBot, useSeo } from "../../lib/hooks";
import { Brand } from "../../components/site/SiteLayout";
import { Field, inputCls, btnPrimary, btnOutline } from "../../components/site/ui";
import { TotpSetup, RecoveryCodes } from "../portal/Security";

const Shell = ({ title, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground grain p-12">
      <Brand light />
      <div><p className="font-display text-4xl leading-tight">Area riservata clienti</p><p className="mt-4 text-white/75 max-w-md">Documenti, richieste e comunicazioni dello studio in un unico spazio protetto.</p></div>
      <p className="text-xs text-white/60">Lo studio non ti chiederà mai password o codici via email o telefono.</p>
    </div>
    <main id="main" className="flex items-center justify-center p-5 sm:p-10">
      <div className="w-full max-w-md">
        <div className="lg:hidden mb-8"><Brand /></div>
        <h1 className="text-3xl sm:text-4xl font-semibold">{title}</h1>
        <div className="mt-8">{children}</div>
        <p className="mt-10 text-sm"><Link to="/" className="underline">← Torna al sito</Link> · <Link to="/guida-portale" className="underline">Guida</Link></p>
      </div>
    </main>
  </div>
);

export function Login() {
  useSeo("Accedi", "", true);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { setState } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfa, setMfa] = useState(null);
  const [method, setMethod] = useState("totp");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const done = (data) => {
    setState({ user: data.user, mfaSetup: data.mfa_setup_required });
    if (data.mfa_setup_required) return nav("/configura-mfa");
    const next = sp.get("next");
    nav(next && next.startsWith("/") ? next : isStaff(data.user) ? "/admin" : "/area-clienti");
  };
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) { setMfa(data); setMethod(data.methods[0]); } else done(data);
    } catch (x) { setErr(errMsg(x)); }
    setBusy(false);
  };
  const verify = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, method, code }); done(data); }
    catch (x) { setErr(errMsg(x)); }
    setBusy(false);
  };
  const sendEmail = async () => {
    setErr("");
    try { await api.post("/auth/mfa/email/send", { mfa_token: mfa.mfa_token }); setInfo("Codice inviato al tuo indirizzo email. Scade tra 10 minuti."); } catch (x) { setErr(errMsg(x)); }
  };

  if (mfa) return (
    <Shell title="Verifica in due passaggi">
      <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Metodo di verifica">
        {mfa.methods.map((m) => (
          <button key={m} role="tab" aria-selected={method === m} onClick={() => { setMethod(m); setCode(""); setInfo(""); }} className={`tap px-3 rounded-md border text-sm inline-flex items-center gap-2 ${method === m ? "bg-primary text-primary-foreground" : ""}`} data-testid={`mfa-method-${m}`}>
            {m === "totp" ? <ShieldCheck className="h-4 w-4" /> : m === "email" ? <Mail className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
            {m === "totp" ? "App Authenticator" : m === "email" ? "Codice via email" : "Codice di recupero"}
          </button>
        ))}
      </div>
      <form onSubmit={verify} className="space-y-4">
        {method === "email" && <button type="button" onClick={sendEmail} className={btnOutline} data-testid="mfa-send-email-btn">Invia codice via email</button>}
        {info && <p role="status" className="text-sm">{info}</p>}
        <Field id="code" label={method === "recovery" ? "Codice di recupero" : "Codice a 6 cifre"}>
          <input id="code" className={`${inputCls} font-mono-ui tracking-widest text-lg`} inputMode={method === "recovery" ? "text" : "numeric"} autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus data-testid="mfa-code-input" />
        </Field>
        {err && <p role="alert" className="text-sm text-destructive" data-testid="mfa-error">{err}</p>}
        <button className={`${btnPrimary} w-full`} disabled={busy} data-testid="mfa-verify-btn">Verifica e accedi</button>
      </form>
      <p className="text-xs text-muted-foreground mt-6">Hai perso il dispositivo? Usa un codice di recupero oppure contatta lo studio per una verifica dell'identità.</p>
    </Shell>
  );
  return (
    <Shell title="Accedi all'area clienti">
      <form onSubmit={submit} className="space-y-4">
        <Field id="email" label="Email"><input id="email" type="email" autoComplete="username" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" /></Field>
        <Field id="password" label="Password"><input id="password" type="password" autoComplete="current-password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" /></Field>
        {err && <p role="alert" className="text-sm text-destructive" data-testid="login-error">{err}</p>}
        <button className={`${btnPrimary} w-full`} disabled={busy} data-testid="login-submit-btn">{busy ? "Accesso…" : "Accedi"}</button>
      </form>
      <p className="mt-5 text-sm"><Link to="/password-dimenticata" className="underline" data-testid="forgot-password-link">Password dimenticata?</Link></p>
      <p className="mt-3 text-xs text-muted-foreground">Gli account sono creati dallo studio. Se non hai ancora un accesso, contatta lo studio.</p>
    </Shell>
  );
}

export function Forgot() {
  useSeo("Password dimenticata", "", true);
  const ab = useAntiBot();
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await api.post("/auth/forgot", { email, ...(await ab.payload()) }); setMsg(data.message); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <Shell title="Password dimenticata">
      {msg ? <p role="status" data-testid="forgot-success">{msg}</p> : (
        <form onSubmit={submit} className="space-y-4 relative">{ab.field}
          <Field id="email" label="Email dell'account"><input id="email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></Field>
          {err && <p role="alert" className="text-sm text-destructive">{err}</p>}
          <button className={`${btnPrimary} w-full`} data-testid="forgot-submit-btn">Invia collegamento</button>
        </form>
      )}
    </Shell>
  );
}

export function SetPassword({ mode }) {
  useSeo(mode === "reset" ? "Reimposta password" : "Attiva account", "", true);
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw !== pw2) return setErr("Le due password non coincidono.");
    try { await api.post(mode === "reset" ? "/auth/reset" : "/auth/activate", { token: sp.get("token"), password: pw }); setOk(true); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <Shell title={mode === "reset" ? "Reimposta la password" : "Attiva il tuo account"}>
      {ok ? <div role="status" data-testid="setpw-success"><p>Password salvata correttamente.</p><Link to="/accedi" className={`${btnPrimary} mt-4`}>Vai all'accesso</Link></div> : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="pw" label="Nuova password" hint="Almeno 10 caratteri, con lettere e numeri."><input id="pw" type="password" autoComplete="new-password" className={inputCls} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="setpw-input" /></Field>
          <Field id="pw2" label="Ripeti la password"><input id="pw2" type="password" autoComplete="new-password" className={inputCls} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="setpw-confirm-input" /></Field>
          {err && <p role="alert" className="text-sm text-destructive" data-testid="setpw-error">{err}</p>}
          <button className={`${btnPrimary} w-full`} data-testid="setpw-submit-btn">Salva password</button>
        </form>
      )}
    </Shell>
  );
}

export function ForcedMfa() {
  useSeo("Configura autenticazione", "", true);
  const { refresh, logout, user } = useAuth();
  const nav = useNavigate();
  const [codes, setCodes] = useState(null);
  if (!user) return <Shell title="Sessione scaduta"><Link to="/accedi" className="underline">Accedi</Link></Shell>;
  return (
    <Shell title="Configura l'app Authenticator">
      <p className="text-sm mb-6">Per il personale dello studio l'autenticazione tramite app è obbligatoria. Completa la configurazione per continuare.</p>
      {codes ? <><RecoveryCodes codes={codes} /><button className={`${btnPrimary} mt-6 w-full`} onClick={async () => { await refresh(); nav("/admin"); }} data-testid="forced-mfa-continue">Ho salvato i codici, continua</button></>
        : <TotpSetup onDone={setCodes} />}
      <button className="mt-6 text-sm underline" onClick={logout}>Esci</button>
    </Shell>
  );
}
